/** @typedef {import('../types/auth').LoginRequest} LoginRequest */
/** @typedef {import('../types/auth').LoginResponse} LoginResponse */
/** @typedef {import('../types/auth').SignupRequest} SignupRequest */
/** @typedef {import('../types/auth').MerchantUser} MerchantUser */
/** @typedef {import('../types/auth').MerchantMetaData} MerchantMetaData */
/** @typedef {import('../types/api').ApiResponse} ApiResponse */

import { instance } from '../config/api';

/**
 * @param {LoginRequest} body
 * @returns {Promise<ApiResponse<LoginResponse>>}
 */
const login = async (body) => {
  const { data } = await instance.post('auth/login', body);
  return data;
};

/**
 * @param {SignupRequest} body
 * @returns {Promise<ApiResponse<MerchantUser>>}
 */
const signup = async (body) => {
  const { data } = await instance.post('auth/signup', body);
  return data;
};

/**
 * @param {{ current_password: string, new_password: string }} body
 * @returns {Promise<ApiResponse<Object>>}
 */
const updatePassword = async (body) => {
  const { data } = await instance.patch('auth/password/update', body);
  return data;
};

/**
 * @param {{ password: string }} body
 * @returns {Promise<ApiResponse<{ qr_code: string, secret: string }>>}
 */
const enable2fa = async (body) => {
  const { data } = await instance.post('auth/twofa/enable', body);
  return data;
};

/**
 * @param {{ token: string }} body
 * @returns {Promise<ApiResponse<Object>>}
 */
const complete2fa = async (body) => {
  const { data } = await instance.post('auth/twofa/complete', body);
  return data;
};

/**
 * @param {{ password: string }} body
 * @returns {Promise<ApiResponse<Object>>}
 */
const disable2fa = async (body) => {
  const { data } = await instance.post('auth/twofa/disable', body);
  return data;
};

/**
 * @param {{ token: string }} body
 * @returns {Promise<ApiResponse<LoginResponse>>}
 */
const verify2faCode = async (body) => {
  const { data } = await instance.post('auth/twofa/verify', body);
  return data;
};

/**
 * @param {{ token: string }} body
 * @returns {Promise<ApiResponse<Object>>}
 */
const verifyEmail = async (body) => {
  const { data } = await instance.post('auth/verification', body);
  return data;
};

/** @returns {Promise<ApiResponse<Object>>} */
const resendVerificationLink = async () => {
  const { data } = await instance.post('auth/resend-verification-link');
  return data;
};

/**
 * @param {{ email: string }} body
 * @returns {Promise<ApiResponse<Object>>}
 */
const requestPasswordReset = async (body) => {
  const { data } = await instance.post('auth/password-reset/request', body);
  return data;
};

/**
 * @param {{ token: string, password: string }} body
 * @returns {Promise<ApiResponse<Object>>}
 */
const changePassword = async (body) => {
  const { data } = await instance.post('auth/password-reset/confirm', body);
  return data;
};

/** @returns {Promise<ApiResponse<MerchantUser>>} */
const fetchMerchantProfile = async () => {
  const { data } = await instance.get('auth/profile');
  return data;
};

/** @returns {Promise<ApiResponse<MerchantUser>>} */
const refreshMerchantUserDetails = async () => {
  const { data } = await instance.get('auth/refresh');
  return data;
};

/**
 * @param {{ email: string, merchant_name: string, country_id: number }} body
 * @returns {Promise<ApiResponse<Object>>}
 */
const signupV2 = async (body) => {
  const { data } = await instance.post('auth/create-account', body);
  return data;
};

/**
 * @param {{ email: string, otp: string }} body
 * @returns {Promise<ApiResponse<LoginResponse>>}
 */
const verifyOTP = async (body) => {
  const { data } = await instance.post('auth/create-account/verify-otp', body);
  return data;
};

/**
 * @param {{ email: string }} body
 * @returns {Promise<ApiResponse<Object>>}
 */
const resendOTP = async (body) => {
  const { data } = await instance.post('auth/create-account/resend-otp', body);
  return data;
};

export {
  changePassword,
  complete2fa,
  disable2fa,
  enable2fa,
  fetchMerchantProfile,
  login,
  refreshMerchantUserDetails,
  requestPasswordReset,
  resendOTP,
  resendVerificationLink,
  signup,
  signupV2,
  updatePassword,
  verify2faCode,
  verifyEmail,
  verifyOTP
};
